// EventCalendar.js
import React, { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createSvgIcon } from '@mui/material/utils';
import TextField from '@mui/material/TextField';
import Card from "olive-react-myoneui/lib/Card";
import Button from "olive-react-myoneui/lib/Button";
import EventMonths from "./EventMonths";
import {
  leftArrow,
  vCopyIcon
} from "../../../constants/icons";
import EventWeekdays from "./EventWeekDays";
import EventsList from "./EventsList";

function EventCalendar({ className }) {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isViewAllEvents, setIsViewAllEvents] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [isWeekView, setIsWeekView] = useState(true);
  const [isDateSelected, setIsDateSelected] = useState(false); // New flag to track if a date was selected

  const toggleView = () => {
    setIsWeekView(!isWeekView);
    setIsDateSelected(false); // Reset selection flag on manual toggle
  };

  const handleViewAllEventsClick = () => {
    setIsViewAllEvents(true); 
  };

  const handleBackToCalendarClick = () => {
    setIsViewAllEvents(false); 
  };

  const handleDaySelect = (day) => {
    setSelectedDay(day);
  };

  const handleDayClick = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    setIsWeekView(false); // Show <EventMonths /> when a date is selected
    setIsDateSelected(true); // Set flag to indicate a date selection occurred
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const eventDates = [
    new Date(2024, 9, 1),
    new Date(2024, 9, 3),
    new Date(2024, 9, 4),
    new Date(2024, 8, 15),
    new Date(2024, 8, 22),
    new Date(2024, 8, 12)
  ];

  const events = [
    {
      id: 1,
      eventTitle: "Interface Design Assignment",
      eventTimeSlot: "10:30 AM - 12:30 PM",
      eventDate: "Oct 03, 2024",
      className: "mb-3 mark-success"
    },
    {
      id: 2,
      eventTitle: "Assignment 01",
      eventTimeSlot: "10:30 AM - 12:30 PM",
      eventDate: "Oct 03, 2024",
      className: "mb-3 mark-progress"
    },
    {
      id: 3,
      eventTitle: "Ut enim ad minim veniam, quis nostrud exercitati",
      eventTimeSlot: "10:30 AM - 12:30 PM",
      eventDate: "Oct 03, 2024",
      className: "mb-3"
    },
    {
      id: 4,
      eventTitle: "Assignment 02",
      eventTimeSlot: "10:30 AM - 12:30 PM",
      eventDate: "Oct 03, 2024",
      className: "mb-3"
    },
    {
      id: 5,
      eventTitle: "Preparatory Batch for Judiciary Exams Explanation",
      eventTimeSlot: "10:30 AM - 12:30 PM",
      eventDate: "Oct 03, 2024",
      className: "mb-3"
    }
  ];

  const DropDownArrow = createSvgIcon(
    <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"/>,
    'DropDownArrow',
  );

  return (
    <Card className={`${className || ""} myk_stu-calendar-wrapper bg-transparent border-0 mt-4`}>
      <Card.Body className="pt-0 px-0">
        <Card.Header className="d-flex align-items-center justify-content-between mb-3 px-1">
          {isViewAllEvents && <h4>All Events</h4>}
          {!isViewAllEvents && (
            <div className="date-picker event-date-picker d-flex align-items-center gap-2">
              <div className="date-picker">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    views={['year', 'month']}
                    format="MMM yyyy"
                    value={selectedDate}
                    onChange={handleDateChange} // Updated to use handleDateChange
                    textField={(params) => <TextField {...params} />}
                    slots={{ openPickerIcon: DropDownArrow }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          )}
          <div className="date-period-arrow-actions d-flex align-items-center gap-2">
            {!isViewAllEvents && (
              <Button variant="outline" className="btn-sm d-flex align-items-center gap-2 bg-transparent" onClick={handleViewAllEventsClick}>
                {screenWidth > 1368 ? (
                  <>
                    View all Events
                  </>
                ) : (
                  'All Events'
                )}
              </Button>
            )}
            {isViewAllEvents &&
            <Button variant="outline" className="btn-sm d-flex align-items-center gap-2 bg-transparent" onClick={handleBackToCalendarClick}>
              {leftArrow} Back to Calendar
            </Button>
            }
            {!isViewAllEvents && (
              <div className="date-period">
                <Button variant="outline" className="btn btn-sm border-0 d-flex align-items-center gap-2" onClick={toggleView}>
                  {isWeekView ? "Week" : "Month"}  
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="9.718"
                    height="4.631"
                    viewBox="0 0 9.718 4.631"
                  >
                    <path
                      id="Path_40581"
                      data-name="Path 40581"
                      d="M7.756,2.95,3.879,0,0,2.95"
                      transform="translate(8.737 3.931) rotate(180)"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.4"
                    />
                  </svg>
                </Button>
              </div>
            )}
          </div>
        </Card.Header>
        {!isViewAllEvents && (
          !isWeekView || isDateSelected // Show <EventMonths /> if a date was selected
          ? <EventMonths selectedDate={selectedDate} eventDates={eventDates} onDaySelect={handleDaySelect} onDayClick={handleDayClick}/> 
          : <EventWeekdays selectedDay={selectedDay} eventDates={eventDates} onDayClick={handleDayClick}/> 
        )}
        <EventsList className={`${isViewAllEvents ? "only-events" : isWeekView ? "" : "month-selected"}`} events={events}/>
      </Card.Body>
    </Card>
  );
}

export default EventCalendar;
